import { Button, Text, Section } from '@/shared/ui/primitives';
import type { FC } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Controller, useForm } from 'react-hook-form';

import { Page } from '@/app/Page';
import { FieldError, DialogBox, SectionTextField } from '@/shared/ui';
import type { Pass, PassType } from '@/types';
import {
  createPassDraftFromTemplate,
  passDraftToCreateRequest,
  PassListItem,
  PassModal,
  PassesEmptyState,
} from '@/features/passes';
import { PassTemplateSelector } from '@/features/pass-types';
import { useCreateClient } from '@/features/clients';
import type { CreateClientRequest } from '@/shared/api';
import { clearDraft, useDraftForm } from '@/shared/hooks/useDraftForm';
import { paths, type AddClientLocationState } from '@/navigation/paths';
import styles from './AddClientPage.module.css';

type AddClientFormValues = {
  name: string;
};

const defaultValues: AddClientFormValues = {
  name: '',
};

export const AddClientPage: FC = () => {
  const [selectedPass, setSelectedPass] = useState<Pass | null>(null);
  const [isTemplateSelectorOpen, setIsTemplateSelectorOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNoPassDialogOpen, setIsNoPassDialogOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<AddClientFormValues | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const draftKey = 'add-client';
  const createInFlight = useRef(false);
  const nameErrorId = useId();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<AddClientFormValues>({
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const createClientMutation = useCreateClient({
    onSuccess: () => {
      clearDraft(draftKey);
      void navigate(paths.home());
    },
  });

  useDraftForm<AddClientFormValues>({
    key: draftKey,
    watch,
    reset,
    enabled: true,
  });

  const handleSave = async (
    values: AddClientFormValues,
    skipNoPassConfirm = false
  ): Promise<boolean> => {
    if (createInFlight.current) return false;
    const trimmedName = values.name.trim();
    if (!trimmedName) return false;
    if (!selectedPass && !skipNoPassConfirm) {
      setPendingValues(values);
      setIsNoPassDialogOpen(true);
      return false;
    }

    const input: CreateClientRequest = selectedPass
      ? {
          name: trimmedName,
          pass: passDraftToCreateRequest(selectedPass),
        }
      : {
          name: trimmedName,
        };

    createInFlight.current = true;
    clearErrors('root');
    try {
      await createClientMutation.mutateAsync(input);
      return true;
    } catch {
      setError('root', {
        message: 'Could not create the client. Your changes are kept. Try again.',
      });
      return false;
    } finally {
      createInFlight.current = false;
    }
  };

  const submitForm = (skipNoPassConfirm = false): void => {
    if (createInFlight.current) return;
    void handleSubmit(async (values) => {
      await handleSave(values, skipNoPassConfirm);
    })();
  };

  const confirmCreateWithoutPass = async (): Promise<void> => {
    if (!pendingValues || createInFlight.current) return;
    if (await handleSave(pendingValues, true)) {
      setIsNoPassDialogOpen(false);
      setPendingValues(null);
    }
  };

  const cancelCreateWithoutPass = (): void => {
    if (createInFlight.current) return;
    setIsNoPassDialogOpen(false);
    setPendingValues(null);
  };

  const handlePassClick = (): void => {
    if (createInFlight.current) return;
    setIsEditModalOpen(true);
  };

  const handleTemplateSelect = (template: PassType): void => {
    setSelectedPass(createPassDraftFromTemplate(template));
  };

  useEffect(() => {
    const state = (location.state as AddClientLocationState | null) || {};
    if (!state.createdTemplate) return;

    const newPass = createPassDraftFromTemplate(state.createdTemplate, {
      id: state.createdTemplate.id,
    });
    setSelectedPass(newPass);
    void navigate('.', { replace: true, state: {} });
  }, [location.state, navigate]);

  const handleEditPass = (passData: Omit<Pass, 'id'>): void => {
    if (!selectedPass) return;
    setSelectedPass({ ...passData, id: selectedPass.id });
    setIsEditModalOpen(false);
  };

  const handleDeletePass = (): void => {
    setSelectedPass(null);
  };

  const handleCloseEditModal = (): void => {
    setIsEditModalOpen(false);
  };

  return (
    <Page back={true}>
      <div className={styles.container}>
        <Text variant="title" as="h1" className={styles.heading} weight="bold">
          New client
        </Text>
        <form
          className={styles.form}
          onSubmit={(event) => {
            event.preventDefault();
            if (!createClientMutation.isPending) submitForm();
          }}
        >
          <Controller
            name="name"
            control={control}
            rules={{
              required: 'Name is required',
              validate: (value) => (value.trim().length > 0 ? true : 'Name is required'),
            }}
            render={({ field }) => (
              <>
                <SectionTextField
                  {...field}
                  label="Client name"
                  ariaLabel="Client name"
                  autoComplete="name"
                  ariaDescribedBy={errors.name ? nameErrorId : undefined}
                  value={field.value}
                  onChange={field.onChange}
                  invalid={Boolean(errors.name)}
                  disabled={createClientMutation.isPending || isSubmitting}
                />
                <FieldError id={nameErrorId} message={errors.name?.message?.toString()} />
              </>
            )}
          />
          {selectedPass && (
            <Section header="Pass">
              <PassListItem pass={selectedPass} onClick={handlePassClick} />
            </Section>
          )}

          {!selectedPass && (
            <PassesEmptyState
              onAddPass={() => setIsTemplateSelectorOpen(true)}
              disabled={createClientMutation.isPending || isSubmitting}
            />
          )}

          <div className={styles.saveWrapper}>
            <FieldError message={errors.root?.message} />
            <Button
              fullWidth
              type="submit"
              disabled={createClientMutation.isPending || isSubmitting}
              loading={createClientMutation.isPending || isSubmitting}
            >
              {createClientMutation.isPending || isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>

        <PassTemplateSelector
          isOpen={isTemplateSelectorOpen}
          onClose={() => setIsTemplateSelectorOpen(false)}
          onSelect={handleTemplateSelect}
          returnTo="/add-client"
        />

        <PassModal
          isOpen={isEditModalOpen}
          mode="edit"
          creditsMode="total"
          onClose={handleCloseEditModal}
          onSave={handleEditPass}
          onDelete={handleDeletePass}
          editPass={selectedPass ?? undefined}
        />

        <DialogBox
          isOpen={isNoPassDialogOpen}
          message={
            <>
              No pass selected. Create client without a pass?
              <FieldError message={errors.root?.message} />
            </>
          }
          confirmText="Create"
          isLoading={createClientMutation.isPending}
          onConfirm={() => void confirmCreateWithoutPass()}
          onCancel={cancelCreateWithoutPass}
        />
      </div>
    </Page>
  );
};
